import { ChangeEvent, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { DownloadIcon } from '../../../../assets/svg';
import { ETPayTableBodySkeleton } from '../../../../components/skeleton-placeholder';
import { ETPayTableProps } from '../../../../models/table';
import { ETPayTableBody } from './components/body';
import { DateFilter } from './components/date-filter';
import { ETPayEmptyBody } from './components/empty-body';
import { ETPayFilters } from './components/filters';
import { ETPayTableHeader } from './components/header';
import { ETPAyPagination } from './components/pagination';
import {
	CustomActionButton,
	CustomExportButton,
	DateFilterExportWrapper,
	Table,
	TableContainer,
	TableHeader,
	TableTitle,
	TableWrapper,
} from './styles';

const CustomPLTable = <Entity,>({
	allowCustomaAction = false,
	allowCustomFilter,
	allowExport = false,
	exportWithSearch = false,
	checkboxSelectedCallback,
	columns,
	customAction,
	customActionText = 'action',
	customFilter,
	data,
	disabledRow,
	emptyMessage,
	exportAction,
	filters,
	hasCheckbox = false,
	hasDateFilter = false,
	hasFilters = false,
	hasPagination = false,
	hasPaginationSelect = true,
	hasSearch = false,
	isLoading,
	maxDate,
	onFilterChange,
	optionsTop,
	pagination,
	pastDate,
	row,
	secondaryAction,
	setPagination,
	stateAll = true,
	stateList,
	statusContent,
	statusWithCircle,
	thisMonth,
	title,
	today,
	totalResults,
}: ETPayTableProps<Entity>) => {
	const theme = useTheme();
	const [checkboxSelected, setCheckboxSelected] = useState<string[]>([]);
	const totalColumns = hasCheckbox ? columns.length + 1 : columns.length;

	const handleCheckboxClicked = (e: ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		if (name === 'all') {
			if (checkboxSelected.length === data.length) {
				setCheckboxSelected([]);
			} else {
				setCheckboxSelected(data.map((_, i) => `checkbox-${i}`));
			}
			return;
		}

		if (checkboxSelected.includes(name)) {
			setCheckboxSelected(checkboxSelected.filter((item) => item !== name));
		} else {
			setCheckboxSelected([...checkboxSelected, name]);
		}
	};

	const handleDisabledExport = (): boolean => {
		return data.length === 0 || isLoading || !allowExport;
	};

	useEffect(() => {
		if (checkboxSelectedCallback) {
			checkboxSelectedCallback(checkboxSelected);
		}
	}, [checkboxSelected]);

	return (
		<TableContainer>
			{optionsTop && optionsTop}

			{(title || hasFilters || hasDateFilter) && (
				<TableHeader>
					{title && <TableTitle>{title}</TableTitle>}
					{secondaryAction && secondaryAction}

					<DateFilterExportWrapper>
						{hasFilters && hasDateFilter && (
							<DateFilter
								filters={filters}
								onFilterChange={onFilterChange}
								pagination={pagination}
								setPagination={setPagination}
								today={today}
								pastDate={pastDate}
								thisMonth={thisMonth}
								maxDate={maxDate}
							/>
						)}

						{allowExport && exportAction && !exportWithSearch && (
							<CustomExportButton onClick={exportAction} disabled={handleDisabledExport()}>
								<DownloadIcon color={theme.colors.extra.space_600} /> Exportar
							</CustomExportButton>
						)}

						{allowCustomaAction && customAction && (
							<CustomActionButton onClick={customAction}>{customActionText}</CustomActionButton>
						)}
					</DateFilterExportWrapper>
				</TableHeader>
			)}

			{hasFilters && (
				<ETPayFilters
					stateAll={stateAll}
					stateList={stateList}
					filters={filters}
					onFilterChange={onFilterChange}
					pagination={pagination}
					setPagination={setPagination}
					hasSearch={hasSearch}
					isLoading={isLoading}
					customFilter={customFilter}
					allowCustomFilter={allowCustomFilter}
					statusContent={statusContent}
					statusWithCircle={statusWithCircle}
					hasExport={allowExport && exportWithSearch}
					exportAction={exportAction}
					handleDisabledExport={handleDisabledExport}
				/>
			)}

			<TableWrapper>
				<Table cellSpacing="0" cellPadding="0">
					<ETPayTableHeader<Entity>
						dataCount={data.length}
						columns={columns}
						isLoading={isLoading}
						hasCheckbox={hasCheckbox}
						checkedSelect={checkboxSelected}
						filters={filters}
						onFilterChange={onFilterChange}
						handleCheckboxClicked={handleCheckboxClicked}
					/>
					{isLoading ? (
						<ETPayTableBodySkeleton columnsLength={totalColumns} />
					) : (
						<ETPayTableBody
							hasCheckbox={hasCheckbox}
							data={data}
							columns={columns}
							row={row}
							checkboxSelected={checkboxSelected}
							handleCheckboxClicked={handleCheckboxClicked}
							disabledRow={disabledRow}
						/>
					)}
				</Table>

				<ETPayEmptyBody<Entity>
					data={data}
					isLoading={isLoading}
					filters={filters}
					emptyMessage={emptyMessage}
				/>

				{hasPagination && (
					<ETPAyPagination
						isLoading={isLoading}
						totalResults={totalResults ?? 0}
						pagination={pagination}
						hasPaginationSelect={hasPaginationSelect}
						setPagination={setPagination}
					/>
				)}
			</TableWrapper>
		</TableContainer>
	);
};

export { CustomPLTable };
